import React, { useMemo, useState } from "react";
import {
  Box,
  FormControl,
  Grid,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PrintIcon from "@mui/icons-material/Print";
import useFetchObjects from "../../api/useFetchObjects";
import ErrorPage from "../Components/ErrorPage";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";

const normalizeList = (data) => (Array.isArray(data) ? data : data?.results || []);

const formatAmount = (value) =>
  Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

const AgentPaymentsIndex = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { data: customerPaymentsData, isLoading: customerLoading, isError: customerError } =
    useFetchObjects(
      ["agent-payment-list-customers"],
      "shop/customer-balance-payments/?page_size=all",
      token
    );
  const { data: supplierPaymentsData, isLoading: supplierLoading, isError: supplierError } =
    useFetchObjects(
      ["agent-payment-list-suppliers"],
      "shop/account-transactions/?page_size=all&transaction_type=debit&search=Supplier%20payment",
      token
    );

  const isLoading = customerLoading || supplierLoading;
  const isError = customerError || supplierError;

  const paymentRows = useMemo(() => {
    const customerRows = normalizeList(customerPaymentsData).map((payment) => ({
      id: `customer-${payment.id}`,
      type: "Customer",
      agentName: payment?.customer_details?.name || payment?.customer_name || "-",
      account: payment?.account_details?.name || "-",
      amount: payment?.amount,
      currency: payment?.currency_details?.title || payment?.currency || "-",
      paymentDate: payment?.payment_date,
      notes: payment?.notes || "-",
    }));

    const supplierRows = normalizeList(supplierPaymentsData).map((payment) => ({
      id: `supplier-${payment.id}`,
      type: "Supplier",
      agentName: payment?.description || "-",
      account: payment?.account_name || "-",
      amount: payment?.amount,
      currency: payment?.transaction_currency_title || payment?.transaction_currency || "-",
      paymentDate: payment?.transaction_date,
      notes: payment?.description || "-",
    }));

    const merged = [...customerRows, ...supplierRows];
    merged.sort((a, b) => {
      const aTime = new Date(a.paymentDate || 0).getTime() || 0;
      const bTime = new Date(b.paymentDate || 0).getTime() || 0;
      return bTime - aTime;
    });

    if (!searchTerm.trim()) return merged;

    const q = searchTerm.trim().toLowerCase();
    return merged.filter((row) =>
      [row.type, row.agentName, row.account, row.amount, row.currency, row.paymentDate, row.notes]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [customerPaymentsData, supplierPaymentsData, searchTerm]);

  const totalCount = paymentRows.length;
  const pagedRows = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return paymentRows.slice(start, start + rowsPerPage);
  }, [paymentRows, page, rowsPerPage]);

  const handlePrintPayment = (row) => {
    if (!row) return;

    const printWindow = window.open("", "_blank", "width=900,height=700");
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <title>Payment Record</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 24px;
              color: #111;
            }
            h1 {
              margin: 0 0 12px;
              font-size: 24px;
            }
            .meta {
              color: #555;
              margin-bottom: 18px;
              font-size: 13px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
              vertical-align: top;
              font-size: 13px;
            }
            th {
              background: #355d31;
              color: #fff;
              width: 180px;
            }
            td.value {
              text-align: left;
            }
          </style>
        </head>
        <body>
          <h1>Payment Record</h1>
          <div class="meta">Printed on ${new Date().toLocaleString()}</div>
          <table>
            <thead>
              <tr>
                <th>Field</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr><th>Type</th><td class="value">${row.type}</td></tr>
              <tr><th>Agent Name</th><td class="value">${row.agentName}</td></tr>
              <tr><th>Account</th><td class="value">${row.account}</td></tr>
              <tr><th>Amount</th><td class="value">${formatAmount(row.amount)}</td></tr>
              <tr><th>Currency</th><td class="value">${row.currency}</td></tr>
              <tr><th>Date</th><td class="value">${formatDateTime(row.paymentDate)}</td></tr>
              <tr><th>Notes</th><td class="value">${row.notes}</td></tr>
            </tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  if (isError) {
    return <ErrorPage message={t("agent.failedToLoadAgents", "Failed to load payments")} />;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h4">{t("agent.paymentRecords", "Payment Records")}</Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>
            {t("agent.dataLoadedOn", "Data loaded on")} {new Date().toLocaleString()}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mb: 2 }}>
        <TextField
          variant="outlined"
          fullWidth
          placeholder={t("agent.searchPayments", "Search payments")}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table aria-label="agent payment records">
          <TableHead>
              <TableRow sx={{ backgroundColor: "primary.main" }}>
              <TableCell sx={{ color: "white" }}>{t("agent.typeLabel", "Type")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("agent.agentName", "Agent Name")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("agent.account", "Account")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("agent.amount", "Amount")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("agent.currency", "Currency")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("agent.paymentDate", "Date")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("agent.notes", "Notes")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("agent.print", "Print")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from(new Array(5)).map((_, index) => (
                  <TableRow key={`payment-skeleton-${index}`}>
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((col) => (
                      <TableCell key={`${index}-${col}`}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : pagedRows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.type}</TableCell>
                    <TableCell>{row.agentName}</TableCell>
                    <TableCell>{row.account}</TableCell>
                    <TableCell>{formatAmount(row.amount)}</TableCell>
                    <TableCell>{row.currency}</TableCell>
                    <TableCell>{formatDateTime(row.paymentDate)}</TableCell>
                    <TableCell>{row.notes}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handlePrintPayment(row)}
                        aria-label={t("agent.print", "Print")}
                        color="primary"
                      >
                        <PrintIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Grid container justifyContent="center" alignItems="center" padding="10px">
        <Box display="flex" alignItems="center" gap={1}>
          <Typography>{t("agent.rowsPerPage", "Rows per page:")}</Typography>
          <FormControl
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  border: "none",
                },
              },
            }}
          >
            <Select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPage(1);
              }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Pagination
          count={Math.ceil(totalCount / rowsPerPage) || 1}
          page={page}
          onChange={(_, nextPage) => setPage(nextPage)}
          variant="outlined"
          color="primary"
          sx={{ ml: 2 }}
        />
      </Grid>
    </Box>
  );
};

export default AgentPaymentsIndex;
